/**
 * FALL LAYOUT — slot generation from run time; objects with bottomCenterPosition, slot, width,
 * and spawnAtTime (seconds from run start).
 * All positional data is relative (0–1). No canvas dimensions.
 */

import * as C from "../config/constants.js";
import { RunBoulder } from "./objects/Boulder.js";
import { RunGem } from "./objects/Gem.js";
import { RunBonus } from "./objects/Bonus.js";
import { RunMergePickup } from "./objects/MergePickup.js";
const DEFAULT_RUN_CONFIG = C.RUN_CONFIG || {};
const Boulder = RunBoulder;
const Gem = RunGem;
const Bonus = RunBonus;
const MergePickup = RunMergePickup;

  function randomRange(min, max) {
    return Math.random() * (max - min) + min;
  }

  function randomInt(min, max) {
    return Math.floor(randomRange(min, max + 1));
  }

  function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
  }

  function shuffleArray(list) {
    var arr = list.slice();
    for (var i = arr.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var tmp = arr[i];
      arr[i] = arr[j];
      arr[j] = tmp;
    }
    return arr;
  }

  function respectsMinGap(position, existingPositions, minGap) {
    for (var i = 0; i < existingPositions.length; i++) {
      if (Math.abs(position - existingPositions[i]) < minGap) return false;
    }
    return true;
  }

  function pickHorizontalPosition(existingPositions, minPos, maxPos, minGap, preferred) {
    if (preferred != null) {
      var preferredClamped = clamp(preferred, minPos, maxPos);
      if (respectsMinGap(preferredClamped, existingPositions, minGap)) return preferredClamped;
    }

    var tries = 24;
    for (var i = 0; i < tries; i++) {
      var candidate = randomRange(minPos, maxPos);
      if (respectsMinGap(candidate, existingPositions, minGap)) return candidate;
    }

    // Deterministic fallback: scan runway and pick farthest point from existing anchors.
    var best = minPos;
    var bestDistance = -1;
    var steps = 40;
    for (var s = 0; s <= steps; s++) {
      var c = minPos + (maxPos - minPos) * (s / steps);
      var minDistanceToExisting = Infinity;
      for (var e = 0; e < existingPositions.length; e++) {
        minDistanceToExisting = Math.min(minDistanceToExisting, Math.abs(c - existingPositions[e]));
      }
      if (existingPositions.length === 0) minDistanceToExisting = Infinity;
      if (minDistanceToExisting > bestDistance) {
        bestDistance = minDistanceToExisting;
        best = c;
      }
    }
    return best;
  }

  function getSpawnProgressBounds(runConfig) {
    var cfg = runConfig || DEFAULT_RUN_CONFIG;
    var runDuration = cfg.RUN_DURATION || 20;
    var firstItemDelaySeconds = Math.max(0, cfg.RUN_TIME_TO_FIRST_ITEM_SECONDS || 0);
    var minProgress = clamp(firstItemDelaySeconds / Math.max(1e-6, runDuration), 0, 0.95);
    // Reserve the last ~1.5 s so objects spawned near the end still have time to fall
    var maxSpawnProgress = clamp(1 - (1.5 / Math.max(1e-6, runDuration)), minProgress + 0.05, 0.95);
    return { minProgress: minProgress, maxProgress: maxSpawnProgress };
  }

  function getBoulderSlots(elements, skipIndex) {
    var slots = {};
    for (var i = 0; i < elements.length; i++) {
      if (i === skipIndex) continue;
      var e = elements[i];
      if (e && e.type === "boulder") slots[e.slotIndex] = true;
    }
    return slots;
  }

  /**
   * Generate slots from timing constraints. Create and fill slots until they fit in run time limit.
   * @returns {{ slots: Array<{progress: number}>, slotCount: number }}
   */
  function generateSlots(runConfig) {
    var cfg = runConfig || DEFAULT_RUN_CONFIG;
    var runDuration = cfg.RUN_DURATION || 20;
    var spawnBounds = getSpawnProgressBounds(cfg);
    var firstItemStartProgress = spawnBounds.minProgress;
    var maxSpawnProgress = spawnBounds.maxProgress;
    var avgReaction = (cfg.REACTION_TIME_MIN + cfg.REACTION_TIME_MAX) / 2;
    var avgBreathing = (cfg.BREATHING_SPACE_MIN + cfg.BREATHING_SPACE_MAX) / 2;
    var cycleTime = avgReaction + avgBreathing;
    var slotCount = clamp(Math.round(runDuration / cycleTime), cfg.RUN_SLOT_COUNT_MIN || 8, cfg.RUN_SLOT_COUNT_MAX || 20);

    var slots = [];
    for (var i = 0; i < slotCount; i++) {
      var t = i / Math.max(1, slotCount - 1);
      var progress = firstItemStartProgress + t * (maxSpawnProgress - firstItemStartProgress);
      slots.push({ progress: progress });
    }
    return { slots: slots, slotCount: slotCount };
  }

  /**
   * Generate run layout: slots + objects. Objects use bottomCenterPosition (0–1), slot, width.
   */
  function generateRunLayout(runConfig) {
    var cfg = runConfig || DEFAULT_RUN_CONFIG;
    var slotResult = generateSlots(cfg);
    var slots = slotResult.slots;
    var slotCount = slotResult.slotCount;
    var availableSlots = Math.max(0, slotCount - 1);

    var boulderMin = cfg.BOULDER_COUNT_MIN ?? cfg.HAZARD_COUNT_MIN ?? 2;
    var boulderMax = cfg.BOULDER_COUNT_MAX ?? cfg.HAZARD_COUNT_MAX ?? 6;
    var hazardCount = randomInt(boulderMin, boulderMax);
    var gemClusterCount = randomInt(cfg.GEM_CLUSTER_COUNT_MIN || 2, cfg.GEM_CLUSTER_COUNT_MAX || 5);
    // Use nullish coalescing so BONUS_COUNT_MAX: 0 (day-1 suppress) is not treated as missing.
    var bonusMin = cfg.BONUS_COUNT_MIN != null ? Number(cfg.BONUS_COUNT_MIN) : 0;
    var bonusMax = cfg.BONUS_COUNT_MAX != null ? Number(cfg.BONUS_COUNT_MAX) : 2;
    if (!isFinite(bonusMin) || bonusMin < 0) bonusMin = 0;
    if (!isFinite(bonusMax) || bonusMax < 0) bonusMax = 0;
    if (bonusMax < bonusMin) bonusMax = bonusMin;
    var bonusCount = hazardCount === 0
      ? 0
      : randomInt(bonusMin, bonusMax);
    var minHorizontalGapSameProgress = cfg.RUNWAY_SAME_PROGRESS_MIN_GAP != null ? cfg.RUNWAY_SAME_PROGRESS_MIN_GAP : 0.3;
    var gemSpacing = cfg.GEM_CLUSTER_SPACING_PROGRESS || 0.008;
    var gemsPerCluster = cfg.GEMS_PER_CLUSTER || 3;
    var maxGemProgressOffset = Math.max(0, gemsPerCluster - 1) * Math.max(0, gemSpacing);
    var maxGemBaseProgress = Math.max(0, 1 - maxGemProgressOffset);
    var gemMaxSlotIndex = 0;
    for (var si = 0; si <= availableSlots; si++) {
      if (slots[si] && slots[si].progress <= maxGemBaseProgress) gemMaxSlotIndex = si;
    }

    var minGemClusterProgressGap = maxGemProgressOffset + Math.max(0, gemSpacing);
    var gemCandidateSlots = [];
    for (var gi = 0; gi <= gemMaxSlotIndex; gi++) gemCandidateSlots.push(gi);
    var shuffledGemCandidates = shuffleArray(gemCandidateSlots);
    var selectedGemSlots = [];
    for (var gc = 0; gc < gemClusterCount; gc++) {
      var selectedSlot = -1;
      for (var c = 0; c < shuffledGemCandidates.length; c++) {
        var candidateIdx = shuffledGemCandidates[c];
        var candidateProgress = slots[candidateIdx].progress;
        var conflicts = selectedGemSlots.some(function (chosenIdx) {
          return Math.abs(candidateProgress - slots[chosenIdx].progress) < minGemClusterProgressGap;
        });
        if (!conflicts) {
          selectedSlot = candidateIdx;
          break;
        }
      }
      if (selectedSlot === -1) break;
      selectedGemSlots.push(selectedSlot);
      shuffledGemCandidates = shuffledGemCandidates.filter(function (idx) {
        return idx !== selectedSlot;
      });
    }

    var hazardCandidateSlots = [];
    for (var hi = 0; hi <= availableSlots; hi++) hazardCandidateSlots.push(hi);
    var shuffledHazardSlots = shuffleArray(hazardCandidateSlots);
    var hazardClusterCount = Math.min(hazardCount, shuffledHazardSlots.length);

    var elements = [];
    for (var i = 0; i < hazardClusterCount; i++) elements.push({ type: "boulder", slotIndex: shuffledHazardSlots[i] });
    for (var i = 0; i < selectedGemSlots.length; i++) elements.push({ type: "gem", slotIndex: selectedGemSlots[i] });
    for (var i = 0; i < bonusCount; i++) elements.push({ type: "bonus", slotIndex: randomInt(0, availableSlots) });

    var earlySlots = Math.min(2, availableSlots);
    var hasEarly = elements.some(function (e) { return e.slotIndex <= earlySlots; });
    if (!hasEarly && elements.length > 0) {
      var moved = false;
      for (var ei = 0; ei < elements.length; ei++) {
        var target = elements[ei];
        if (target.type !== "boulder") continue;
        var usedHazardSlots = getBoulderSlots(elements, ei);
        var earlyHazardChoices = [];
        for (var es = 0; es <= earlySlots; es++) {
          if (!usedHazardSlots[es]) earlyHazardChoices.push(es);
        }
        if (earlyHazardChoices.length > 0) {
          target.slotIndex = earlyHazardChoices[randomInt(0, earlyHazardChoices.length - 1)];
          moved = true;
        }
        break;
      }
      if (!moved) elements[0].slotIndex = randomInt(0, earlySlots);
    }

    var bySlot = {};
    for (var i = 0; i < elements.length; i++) {
      var idx = elements[i].slotIndex;
      if (!bySlot[idx]) bySlot[idx] = [];
      bySlot[idx].push({ type: elements[i].type });
    }

    var objects = [];

    for (var i = 0; i < slotCount; i++) {
      var items = bySlot[i] || [];
      var slotAnchors = [];
      for (var s = 0; s < items.length; s++) {
        var item = items[s];
        var baseProgress = slots[i].progress;

        var runDurationForSpawn = cfg.RUN_DURATION || 20;
        if (item.type === "boulder") {
          var boulderX = pickHorizontalPosition(slotAnchors, 0.15, 0.85, minHorizontalGapSameProgress);
          slotAnchors.push(boulderX);
          objects.push({
            type: "boulder",
            bottomCenterPosition: boulderX,
            slot: i,
            progress: baseProgress,
            spawnAtTime: baseProgress * runDurationForSpawn,
            width: cfg.BOULDER_WIDTH_RATIO || cfg.HAZARD_WIDTH_RATIO || 0.08,
          });
        } else if (item.type === "gem") {
          var gemBottomCenter = pickHorizontalPosition(slotAnchors, 0.15, 0.85, minHorizontalGapSameProgress);
          slotAnchors.push(gemBottomCenter);
          for (var g = 0; g < gemsPerCluster; g++) {
            var gemProgress = Math.min(1, baseProgress + g * gemSpacing);
            objects.push({
              type: "gem",
              bottomCenterPosition: gemBottomCenter,
              slot: i,
              slotOffset: g * gemSpacing,
              progress: gemProgress,
              spawnAtTime: gemProgress * runDurationForSpawn,
              width: cfg.GEM_WIDTH_RATIO || 0.04,
            });
          }
        } else if (item.type === "bonus") {
          var bonusX = pickHorizontalPosition(slotAnchors, 0.15, 0.85, minHorizontalGapSameProgress, 0.5);
          slotAnchors.push(bonusX);
          objects.push({
            type: "bonus",
            bottomCenterPosition: bonusX,
            slot: i,
            progress: baseProgress,
            spawnAtTime: baseProgress * runDurationForSpawn,
            width: cfg.BONUS_WIDTH_RATIO || 0.18,
          });
        }
      }
    }

    return {
      objects: objects,
      slots: slots,
      slotCount: slotCount,
      spawnBounds: getSpawnProgressBounds(cfg),
      runDuration: cfg.RUN_DURATION || 20,
    };
  }

  /**
   * Create fall-stage object instances from layout data.
   * Each instance carries spawnAtTime (seconds from run start).
   */
  function createRunwayObjects(layoutResult, runConfig) {
    var objects = [];
    for (var i = 0; i < layoutResult.objects.length; i++) {
      var d = layoutResult.objects[i];
      var obj;
      if (d.type === "boulder") {
        obj = new Boulder(d.bottomCenterPosition, d.slot, d.progress, d.width);
      } else if (d.type === "gem") {
        obj = new Gem(d.bottomCenterPosition, d.slot, d.slotOffset, d.progress, d.width);
      } else if (d.type === "bonus") {
        obj = new Bonus(d.bottomCenterPosition, d.slot, d.progress, d.width);
      } else {
        continue;
      }
      obj.spawnAtTime = d.spawnAtTime != null ? d.spawnAtTime : (d.progress * (runConfig.RUN_DURATION || 20));
      objects.push(obj);
    }

    return objects;
  }

  /**
   * Create merge pickups. Use bottomCenterPosition (0–1), progress, width.
   */
  function createMergePickups(layoutResult, runConfig, objectMinProgress, objectMaxProgress, pickSpawnTier) {
    var cfg = runConfig || DEFAULT_RUN_CONFIG;
    var minProgress = clamp(objectMinProgress != null ? objectMinProgress : 0, 0, 1);
    var maxProgress = clamp(objectMaxProgress != null ? objectMaxProgress : 1, minProgress, 1);
    var count = randomInt(cfg.MERGE_PICKUP_COUNT_MIN || 50, cfg.MERGE_PICKUP_COUNT_MAX || 150);
    var margin = cfg.MERGE_PICKUP_MARGIN_RATIO || 0.04;
    var offset = cfg.MERGE_PICKUP_OFFSET_RATIO || 0.08;
    var width = cfg.MERGE_PICKUP_WIDTH_RATIO || 0.028;
    var pickups = [];

    var runDuration = cfg.RUN_DURATION || 20;
    for (var i = 0; i < count; i++) {
      var t = (i + 0.5) / count;
      var baseProgress = minProgress + t * (maxProgress - minProgress);
      var progress = clamp(baseProgress + randomRange(-offset, offset), minProgress, maxProgress);
      var bottomCenterPosition = randomRange(margin, 1 - margin);
      var tier = typeof pickSpawnTier === "function" ? pickSpawnTier() : 0;
      var pickup = new MergePickup(progress, bottomCenterPosition, tier, width);
      pickup.spawnAtTime = progress * runDuration;
      pickups.push(pickup);
    }
    return pickups;
  }

export { generateSlots, generateRunLayout, createRunwayObjects, createMergePickups };
